// Activiteiten toevoegen/bewerken: invoer, validatie en omzetting naar een Activity.
// Puur (geen React/opslag). Hergebruikt de opschoonregels uit ./activities.
import {
  ACTIVITY_DUUR_MAX,
  ACTIVITY_DUUR_MIN,
  ACTIVITY_FIELD_LIMITS,
  getMainCategory,
  parseLatLng,
  sanitizeActivity,
} from './activities';
import type { Activity, ActivityType, ActivityWeather, Energy } from './types';

/** Wat het formulier invult. id, score en categorie worden afgeleid. */
export interface ActivityInput {
  naam: string;
  omschrijving: string;
  type: ActivityType;
  weer: ActivityWeather;
  /** "lat, lng" of lege string. */
  locatie: string;
  /** Minuten (1–600). */
  duur: number;
  energie: Energy;
  /** Gekozen spelersaantallen; lege lijst = past bij elk aantal. */
  personen: PersonsOption[];
}

export type PersonsOption = '1' | '2' | '3+';
export const PERSONS_OPTIONS: readonly PersonsOption[] = ['1', '2', '3+'];

export const EMPTY_ACTIVITY_INPUT: ActivityInput = {
  naam: '',
  omschrijving: '',
  type: 'Binnen',
  weer: 'Alles',
  locatie: '',
  duur: 30,
  energie: 'Rustig',
  personen: [],
};

export type ActivityInputField = 'naam' | 'omschrijving' | 'duur' | 'locatie';

export interface ActivityInputErrors {
  /** Per veld een Nederlandse, begrijpelijke melding. Leeg object = geldig. */
  errors: Partial<Record<ActivityInputField, string>>;
  /** Niet-blokkerend, bijv. een naam die al bestaat. */
  warnings: string[];
}

/** Begrenzingen komen uit ACTIVITY_FIELD_LIMITS/ACTIVITY_DUUR_* in ./activities. */
export function validateActivityInput(
  input: ActivityInput,
  opts: { existing: readonly Activity[]; editingId?: number },
): ActivityInputErrors {
  const errors: ActivityInputErrors['errors'] = {};
  const warnings: string[] = [];

  const naam = input.naam.trim();
  if (!naam) errors.naam = 'Geef de activiteit een naam.';
  else if (naam.length > ACTIVITY_FIELD_LIMITS.naam) {
    errors.naam = `De naam is te lang (maximaal ${ACTIVITY_FIELD_LIMITS.naam} tekens).`;
  }

  if (input.omschrijving.trim().length > ACTIVITY_FIELD_LIMITS.omschrijving) {
    errors.omschrijving = `De omschrijving is te lang (maximaal ${ACTIVITY_FIELD_LIMITS.omschrijving} tekens).`;
  }

  const d = input.duur;
  if (!Number.isInteger(d) || d < ACTIVITY_DUUR_MIN || d > ACTIVITY_DUUR_MAX) {
    errors.duur = `Kies een duur van ${ACTIVITY_DUUR_MIN} tot ${ACTIVITY_DUUR_MAX} minuten (hele getallen).`;
  }

  const locatie = input.locatie.trim();
  if (locatie && !parseLatLng(locatie)) {
    errors.locatie = 'Vul de locatie in als breedte- en lengtegraad, bijvoorbeeld 52.51, 6.09.';
  }

  if (naam && !errors.naam) {
    const key = naam.toLowerCase();
    const clash = opts.existing.some((a) => a.id !== opts.editingId && a.naam.trim().toLowerCase() === key);
    if (clash) warnings.push('Er bestaat al een activiteit met deze naam.');
  }

  return { errors, warnings };
}

/** "1, 2, 3+" ↔ ['1','2','3+'] (volgorde 1,2,3+; onbekende waarden genegeerd; legacy "3" telt als "3+"). */
export function parsePersons(value: string): PersonsOption[] {
  const found = new Set<PersonsOption>();
  for (const p of String(value ?? '').split(',').map((s) => s.trim())) {
    if (p === '1' || p === '2') found.add(p);
    else if (p === '3' || p === '3+') found.add('3+');
  }
  return PERSONS_OPTIONS.filter((o) => found.has(o));
}
export function formatPersons(selected: readonly PersonsOption[]): string {
  return PERSONS_OPTIONS.filter((o) => selected.includes(o)).join(', ');
}

export function activityToInput(a: Activity): ActivityInput {
  return {
    naam: a.naam,
    omschrijving: a.omschrijving,
    type: a.type,
    weer: a.weer,
    locatie: a.locatie,
    duur: a.duur,
    energie: a.energie,
    personen: parsePersons(a.aantalPersonen),
  };
}

/** Opgeschoonde Activity uit de invoer (via sanitizeActivity); categorie afgeleid uit type × energie. */
export function inputToActivity(input: ActivityInput, id: number, score = 0): Activity {
  return sanitizeActivity(
    {
      id,
      score,
      naam: input.naam,
      omschrijving: input.omschrijving,
      type: input.type,
      weer: input.weer,
      locatie: input.locatie,
      duur: input.duur,
      energie: input.energie,
      aantalPersonen: formatPersons(input.personen),
      categorie: getMainCategory(input.type, input.energie),
    },
    Math.max(0, id - 1),
  );
}

/** Volgende vrije id (hoogste + 1, minimaal 1). */
export function nextActivityId(list: readonly Activity[]): number {
  return list.reduce((max, a) => (a.id > max ? a.id : max), 0) + 1;
}
